import { Check } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { scrollToId } from "@/components/Navbar";

const TIERS = [
  {
    name: "Standard Fitment",
    price: "120",
    tagline: "You supply the camera",
    features: [
      "Front dash cam installation",
      "100% concealed wiring",
      "12V or basic fuse connection",
      "Lens angle calibration",
      "12-month workmanship warranty",
    ],
    featured: false,
  },
  {
    name: "Dual Channel Pro",
    price: "199",
    tagline: "Our most-booked install",
    features: [
      "Front + rear camera install",
      "Full hardwire kit included",
      "24/7 parking mode enabled",
      "Battery-saver voltage cut-off",
      "App pairing & walkthrough",
      "12-month workmanship warranty",
    ],
    featured: true,
  },
  {
    name: "Supply + Install",
    price: "549",
    tagline: "Camera included, fitted same day",
    features: [
      "4K dual-channel camera supplied",
      "Everything in Dual Channel Pro",
      "High-endurance 64GB+ card",
      "BlackVue / VIOFO / Thinkware / ClearView options",
      "Priority booking window",
    ],
    featured: false,
  },
];

const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="relative border-y border-white/10 bg-carbon/40 overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(#1f2937_1px,transparent_1px)] [background-size:26px_26px] opacity-20 pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
      <SectionHeading eyebrow="Transparent pricing" title="Pick your package" testId="pricing-heading" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch">
        {TIERS.map((tier, i) => (
          <Reveal key={tier.name} delay={i * 0.12} className="h-full">
            <div
              data-testid={`pricing-card-${i + 1}`}
              className={`relative h-full flex flex-col rounded-xl p-8 border transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-1.5 ${
                tier.featured
                  ? "bg-carbon border-sky-500/50 shadow-[0_0_40px_rgba(14,165,233,0.14)]"
                  : "bg-obsidian border-white/10 hover:border-white/25"
              }`}
            >
              {tier.featured && (
                <span className="absolute -top-3 left-8 bg-sky-500 text-obsidian font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}
              <h3 className="font-display font-bold uppercase tracking-wide text-2xl text-gray-50">{tier.name}</h3>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-gray-500">{tier.tagline}</p>
              <p className="mt-6 flex items-end gap-1">
                <span className="font-mono text-xs text-gray-500 mb-2">from</span>
                <span className="font-display font-black text-6xl leading-none text-green-400">
                  ${tier.price}
                </span>
              </p>
              <ul className="mt-8 space-y-3.5 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-gray-300">
                    <Check size={16} className="text-sky-500 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`pricing-enquire-${i + 1}`}
                onClick={() => scrollToId("contact")}
                className={`mt-9 w-full font-display font-bold uppercase tracking-widest text-sm py-3.5 rounded-md transition-[background-color,color,box-shadow] duration-300 ${
                  tier.featured
                    ? "bg-sky-500 text-obsidian hover:bg-sky-400 hover:shadow-[0_0_25px_rgba(14,165,233,0.4)]"
                    : "border border-white/20 text-gray-100 hover:border-sky-500/60 hover:text-sky-400"
                }`}
              >
                Enquire Now
              </button>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.25}>
        <p className="mt-8 font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gray-500" data-testid="pricing-note">
          Final quote depends on vehicle make &amp; model — confirm when you enquire
        </p>
      </Reveal>
    </div>
  </section>
);

export default Pricing;
